import {
  Account,
  Clock,
  ClockAlertOverdue,
  CloseDate,
  Dollar
} from "@dreamhub/designsystem/icons";
import UserAvatar from "@/components/controls/UserAvatar";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { DealColumn } from "@/pages/deals/types";
import { UserData } from "@/entities/users/types";
import { useArrMrr } from "@/services/context/ArrMrrContext/hook";
import { cn } from "@/utils/utils";
import React from "react";
import { useTranslation } from "react-i18next";

interface Props {
  fieldName: string;
  triggerValue?: string;
  tooltipValue?: string;
  ownerData?: UserData;
  isExpectedCloseAtOverdue?: boolean;
  daysAboveAvg?: number;
}

const DetailChip: React.FC<Props> = ({
  fieldName,
  triggerValue,
  tooltipValue,
  ownerData,
  isExpectedCloseAtOverdue = false,
  daysAboveAvg = 0
}) => {
  const { t } = useTranslation();
  const { label: arrOrMrrLabel } = useArrMrr();

  const getTriggerIcon = () => {
    switch (fieldName) {
      case "arr":
      case "mrr":
        return <Dollar />;
      case DealColumn.COMPANY_NAME:
        return <Account />;
      case "timeInStage":
        return daysAboveAvg > 0 ? <ClockAlertOverdue /> : <Clock />;
      case DealColumn.EXPECTED_CLOSE_AT:
        return <CloseDate />;

      default:
        return null;
    }
  };

  const getTriggerComponent = () => {
    switch (fieldName) {
      case DealColumn.MANAGED_BY:
        return <UserAvatar size="S" userData={ownerData} />;

      default:
        return (
          <>
            <IconContainer
              color="tertiary"
              className="transition-all duration-200 ease-in-out"
            >
              {getTriggerIcon()}
            </IconContainer>
            <Text
              as="span"
              variant="bodySecondary"
              className={cn(
                fieldName === "arr" ||
                  (fieldName === "mrr" && "translate-y-[1px]"),
                isExpectedCloseAtOverdue && "text-warning"
              )}
            >
              {triggerValue}
            </Text>
          </>
        );
    }
  };

  const getTooltipTitle = () => {
    if (isExpectedCloseAtOverdue) return "";
    switch (fieldName) {
      case DealColumn.MANAGED_BY:
        return t("deals.managedByTitle");
      case "arr":
      case "mrr":
        return arrOrMrrLabel;

      default:
        return t(`deals.${fieldName}`);
    }
  };

  const getTooltipContent = () => {
    switch (fieldName) {
      case DealColumn.MANAGED_BY:
        return (
          <>
            <UserAvatar size="S" userData={ownerData} />
            <Text>{ownerData?.displayName}</Text>
          </>
        );

      default:
        return (
          <>
            <Text>{tooltipValue}</Text>
            {daysAboveAvg > 0 && (
              <Text variant="bodySecondary">{`${daysAboveAvg} ${t("deals.aboveAvg")}`}</Text>
            )}
          </>
        );
    }
  };

  return (
    <Tooltip delayDuration={600}>
      <TooltipTrigger asChild>
        <span tabIndex={0}>
          <div className="group/chip p-1 flex items-center gap-1 justify-center transition-all duration-200 ease-in-out rounded-[6px]">
            {getTriggerComponent()}
          </div>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <div className="flex flex-col gap-2">
          <Text variant="smBodySecondary">{getTooltipTitle()}</Text>
          <div className="flex gap-2 items-center">{getTooltipContent()}</div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

export default DetailChip;
